import { createStore } from 'zustand/vanilla';
import { useStore } from 'zustand';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { subscribeWithSelector } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import type { DirectMessage } from '../_types';

export type ChatKey = string;

export type MessageMeta = {
  id: number;
  fromUserId: number;
  createdAt: string;
};

export type ChatSlice = {
  orderedIds: number[];
  byId: Record<number, DirectMessage>;
  metaById: Record<number, MessageMeta>;
  byClientId: Record<string, number>;
  lastServerId: number;
  hasMore: boolean;
};

export type UpsertOptions = {
  mode?: 'append' | 'prepend';
  meId?: number;
};

export type UpsertResult = {
  addedIds: number[];
  changedIds: number[];
  replacedIds: number[];
  hasIncoming: boolean;
  maxServerId: number;
};

const EMPTY_ORDERED: number[] = [];
const EMPTY_BY_ID: Record<number, DirectMessage> = {};
const EMPTY_META: Record<number, MessageMeta> = {};
const EMPTY_CHAT: ChatSlice = {
  orderedIds: EMPTY_ORDERED,
  byId: EMPTY_BY_ID,
  metaById: EMPTY_META,
  byClientId: {},
  lastServerId: 0,
  hasMore: true,
};

const isServerId = (id: number) => id > 0 && id < 1_000_000_000_000;

const buildMeta = (msg: DirectMessage): MessageMeta => ({
  id: Number((msg as any)?.id || 0) || 0,
  fromUserId: Number((msg as any)?.from_user?.id || 0) || 0,
  createdAt: String((msg as any)?.created_at || ''),
});

const mergeMessage = (prev: DirectMessage, incoming: DirectMessage): DirectMessage => {
  let changed = false;
  const next: any = { ...(prev as any) };
  const has = (k: string) => Object.prototype.hasOwnProperty.call(incoming as any, k);

  if (has('client_id')) {
    const inc = typeof (incoming as any)?.client_id === 'string' && String((incoming as any).client_id).trim()
      ? String((incoming as any).client_id)
      : (prev as any)?.client_id;
    if (next.client_id !== inc) {
      next.client_id = inc;
      changed = true;
    }
  }

  if (has('local_photo_src')) {
    const inc = (incoming as any).local_photo_src;
    const nextVal = typeof inc === 'string' && inc ? inc : (prev as any)?.local_photo_src;
    if (next.local_photo_src !== nextVal) {
      next.local_photo_src = nextVal;
      changed = true;
    }
  }

  if (has('local_voice_src')) {
    const inc = (incoming as any).local_voice_src;
    const nextVal = typeof inc === 'string' && inc ? inc : (prev as any)?.local_voice_src;
    if (next.local_voice_src !== nextVal) {
      next.local_voice_src = nextVal;
      changed = true;
    }
  }

  for (const [k, v] of Object.entries(incoming as any)) {
    if (k === 'client_id' || k === 'local_photo_src' || k === 'local_voice_src') continue;
    if (v === undefined) continue;
    if ((next as any)[k] !== v) {
      (next as any)[k] = v;
      changed = true;
    }
  }

  const incomingId = Number((incoming as any)?.id || 0) || 0;
  if (isServerId(incomingId) && (next as any).status !== undefined) {
    next.status = undefined;
    changed = true;
  }

  return changed ? (next as DirectMessage) : prev;
};

const createEmptyChat = (): ChatSlice => ({
  orderedIds: [],
  byId: {},
  metaById: {},
  byClientId: {},
  lastServerId: 0,
  hasMore: true,
});

export type ChatMessagesState = {
  chats: Record<ChatKey, ChatSlice>;
  setChatSnapshot: (chatKey: ChatKey, messages: DirectMessage[], opts?: { hasMore?: boolean; lastServerId?: number }) => void;
  upsertMessages: (chatKey: ChatKey, messages: DirectMessage[], opts?: UpsertOptions) => UpsertResult;
  patchMessage: (chatKey: ChatKey, messageId: number, patch: Partial<DirectMessage>) => boolean;
  patchMessageByClientId: (chatKey: ChatKey, clientId: string, patch: Partial<DirectMessage>) => boolean;
  patchMessagesByUser: (chatKey: ChatKey, userId: number, patch: Partial<DirectMessage['from_user']>) => boolean;
  removeMessage: (chatKey: ChatKey, messageId: number) => boolean;
  clearChat: (chatKey: ChatKey) => void;
  setHasMore: (chatKey: ChatKey, hasMore: boolean) => void;
  setLastServerId: (chatKey: ChatKey, lastServerId: number) => void;
};

export const chatMessagesStore = createStore<ChatMessagesState>()(
  subscribeWithSelector((set, get) => ({
    chats: {},
    setChatSnapshot: (chatKey, messages, opts) => {
      const next = createEmptyChat();
      let maxServerId = 0;
      for (const m of messages || []) {
        const id = Number((m as any)?.id || 0) || 0;
        if (!id) continue;
        next.byId[id] = m;
        next.orderedIds.push(id);
        next.metaById[id] = buildMeta(m);
        const cid = typeof (m as any)?.client_id === 'string' ? String((m as any).client_id).trim() : '';
        if (cid) next.byClientId[cid] = id;
        if (isServerId(id)) maxServerId = Math.max(maxServerId, id);
      }
      // Комментарий на русском: сортируем по id (ASC), чтобы старые сообщения были вверху.
      next.orderedIds.sort((a, b) => a - b);
      next.hasMore = typeof opts?.hasMore === 'boolean' ? opts.hasMore : true;
      next.lastServerId = typeof opts?.lastServerId === 'number' ? opts.lastServerId : maxServerId;
      set((state) => ({ chats: { ...state.chats, [chatKey]: next } }));
    },
    upsertMessages: (chatKey, messages, opts) => {
      const result: UpsertResult = { addedIds: [], changedIds: [], replacedIds: [], hasIncoming: false, maxServerId: 0 };
      const mode = opts?.mode === 'prepend' ? 'prepend' : 'append';

      set((state) => {
        const prevChat = state.chats[chatKey] || createEmptyChat();
        let orderedIds = prevChat.orderedIds;
        let byId = prevChat.byId;
        let metaById = prevChat.metaById;
        let byClientId = prevChat.byClientId;
        let lastServerId = prevChat.lastServerId;
        let orderedChanged = false;
        let byIdChanged = false;
        let metaChanged = false;
        let byClientChanged = false;

        const indexById = new Map<number, number>();
        orderedIds.forEach((id, idx) => indexById.set(id, idx));
        const newIds: number[] = [];

        for (const raw of messages || []) {
          const msg = raw as DirectMessage;
          const incomingId = Number((msg as any)?.id || 0) || 0;
          const cid = typeof (msg as any)?.client_id === 'string' ? String((msg as any).client_id).trim() : '';
          let existingId = 0;

          if (incomingId && byId[incomingId]) existingId = incomingId;
          else if (cid && byClientId[cid]) existingId = byClientId[cid];

          if (incomingId && isServerId(incomingId)) {
            lastServerId = Math.max(lastServerId, incomingId);
            result.maxServerId = Math.max(result.maxServerId, incomingId);
          }
          if (opts?.meId && msg?.from_user?.id && msg.from_user.id !== opts.meId) {
            result.hasIncoming = true;
          }

          if (existingId) {
            const prevMsg = byId[existingId] || (msg as DirectMessage);
            const targetId = incomingId || existingId;

            if (existingId !== targetId) {
              if (!orderedChanged) {
                orderedIds = [...orderedIds];
                orderedChanged = true;
              }
              const idx = indexById.get(existingId);
              if (typeof idx === 'number') {
                orderedIds[idx] = targetId;
                indexById.delete(existingId);
                indexById.set(targetId, idx);
              }
              if (!byIdChanged) {
                byId = { ...byId };
                byIdChanged = true;
              }
              delete byId[existingId];
              const merged = mergeMessage(prevMsg, { ...(msg as any), id: targetId } as any);
              byId[targetId] = merged;
              result.replacedIds.push(targetId);
            } else {
              const merged = mergeMessage(prevMsg, msg);
              if (merged !== prevMsg) {
                if (!byIdChanged) {
                  byId = { ...byId };
                  byIdChanged = true;
                }
                byId[existingId] = merged;
                result.changedIds.push(existingId);
              }
            }

            const finalMsg = byId[targetId] || (msg as DirectMessage);
            const nextMeta = buildMeta(finalMsg);
            const prevMeta = metaById[targetId];
            if (!prevMeta || prevMeta.fromUserId !== nextMeta.fromUserId || prevMeta.createdAt !== nextMeta.createdAt) {
              if (!metaChanged) {
                metaById = { ...metaById };
                metaChanged = true;
              }
              metaById[targetId] = nextMeta;
            }

            if (cid) {
              if (!byClientChanged) {
                byClientId = { ...byClientId };
                byClientChanged = true;
              }
              byClientId[cid] = targetId;
            }
            continue;
          }

          if (!incomingId) continue;
          if (!byIdChanged) {
            byId = { ...byId };
            byIdChanged = true;
          }
          byId[incomingId] = msg;
          result.addedIds.push(incomingId);
          newIds.push(incomingId);

          if (!metaChanged) {
            metaById = { ...metaById };
            metaChanged = true;
          }
          metaById[incomingId] = buildMeta(msg);

          if (cid) {
            if (!byClientChanged) {
              byClientId = { ...byClientId };
              byClientChanged = true;
            }
            byClientId[cid] = incomingId;
          }
        }

        if (newIds.length) {
          if (!orderedChanged) {
            orderedIds = [...orderedIds];
            orderedChanged = true;
          }
          // Комментарий на русском: сортируем newIds по id (ASC) перед вставкой.
          newIds.sort((a, b) => a - b);
          orderedIds = mode === 'prepend' ? [...newIds, ...orderedIds] : [...orderedIds, ...newIds];
        }

        if (!orderedChanged && !byIdChanged && !metaChanged && !byClientChanged && lastServerId === prevChat.lastServerId) {
          return state;
        }

        const nextChat: ChatSlice = {
          orderedIds: orderedChanged ? orderedIds : prevChat.orderedIds,
          byId: byIdChanged ? byId : prevChat.byId,
          metaById: metaChanged ? metaById : prevChat.metaById,
          byClientId: byClientChanged ? byClientId : prevChat.byClientId,
          lastServerId,
          hasMore: prevChat.hasMore,
        };
        return { chats: { ...state.chats, [chatKey]: nextChat } };
      });

      return result;
    },
    patchMessage: (chatKey, messageId, patch) => {
      if (!messageId) return false;
      let changed = false;
      set((state) => {
        const chat = state.chats[chatKey];
        if (!chat) return state;
        const prev = chat.byId[messageId];
        if (!prev) return state;
        const merged = mergeMessage(prev, { ...(patch as any), id: messageId } as any);
        if (merged === prev) return state;
        const nextById = { ...chat.byId, [messageId]: merged };
        const nextMeta = buildMeta(merged);
        const prevMeta = chat.metaById[messageId];
        const metaChanged = !prevMeta || prevMeta.fromUserId !== nextMeta.fromUserId || prevMeta.createdAt !== nextMeta.createdAt;
        changed = true;
        return {
          chats: {
            ...state.chats,
            [chatKey]: {
              ...chat,
              byId: nextById,
              metaById: metaChanged ? { ...chat.metaById, [messageId]: nextMeta } : chat.metaById,
            },
          },
        };
      });
      return changed;
    },
    patchMessageByClientId: (chatKey, clientId, patch) => {
      const cid = String(clientId || '').trim();
      if (!cid) return false;
      const chat = get().chats[chatKey];
      const id = chat?.byClientId?.[cid] || 0;
      if (!id) return false;
      return get().patchMessage(chatKey, id, patch);
    },
    patchMessagesByUser: (chatKey, userId, patch) => {
      const uid = Number(userId || 0) || 0;
      if (!uid) return false;
      let changed = false;
      set((state) => {
        const chat = state.chats[chatKey];
        if (!chat) return state;
        let byId = chat.byId;
        let byIdChanged = false;
        for (const id of chat.orderedIds) {
          const cur = byId[id];
          if (!cur || Number((cur as any)?.from_user?.id || 0) !== uid) continue;
          const nextFrom = { ...(cur as any).from_user, ...(patch as any) };
          const nextMsg = mergeMessage(cur, { ...(cur as any), from_user: nextFrom } as any);
          if (nextMsg !== cur) {
            if (!byIdChanged) {
              byId = { ...byId };
              byIdChanged = true;
            }
            byId[id] = nextMsg;
            changed = true;
          }
        }
        if (!byIdChanged) return state;
        return { chats: { ...state.chats, [chatKey]: { ...chat, byId } } };
      });
      return changed;
    },
    removeMessage: (chatKey, messageId) => {
      if (!messageId) return false;
      let changed = false;
      set((state) => {
        const chat = state.chats[chatKey];
        if (!chat || !chat.byId[messageId]) return state;
        const nextById = { ...chat.byId };
        const nextMeta = { ...chat.metaById };
        const nextByClient = { ...chat.byClientId };
        const prev = nextById[messageId];
        const cid = typeof (prev as any)?.client_id === 'string' ? String((prev as any).client_id).trim() : '';
        delete nextById[messageId];
        delete nextMeta[messageId];
        if (cid && nextByClient[cid] === messageId) delete nextByClient[cid];
        const nextOrdered = chat.orderedIds.filter((id) => id !== messageId);
        changed = true;
        return {
          chats: {
            ...state.chats,
            [chatKey]: { ...chat, orderedIds: nextOrdered, byId: nextById, metaById: nextMeta, byClientId: nextByClient },
          },
        };
      });
      return changed;
    },
    clearChat: (chatKey) => {
      set((state) => {
        if (!state.chats[chatKey]) return state;
        const nextChats = { ...state.chats };
        delete nextChats[chatKey];
        return { chats: nextChats };
      });
    },
    setHasMore: (chatKey, hasMore) => {
      set((state) => {
        const chat = state.chats[chatKey];
        if (!chat || chat.hasMore === hasMore) return state;
        return { chats: { ...state.chats, [chatKey]: { ...chat, hasMore } } };
      });
    },
    setLastServerId: (chatKey, lastServerId) => {
      set((state) => {
        const chat = state.chats[chatKey];
        if (!chat || chat.lastServerId === lastServerId) return state;
        return { chats: { ...state.chats, [chatKey]: { ...chat, lastServerId } } };
      });
    },
  }))
);

export const useChatMessagesStore = <T,>(selector: (state: ChatMessagesState) => T, equalityFn?: (a: T, b: T) => boolean) =>
  useStoreWithEqualityFn(chatMessagesStore, selector, equalityFn);

export const useChatOrderedIds = (chatKey: ChatKey) =>
  useStore(chatMessagesStore, (s) => s.chats[chatKey]?.orderedIds ?? EMPTY_ORDERED);

export const useChatMetaById = (chatKey: ChatKey) =>
  useStore(chatMessagesStore, (s) => s.chats[chatKey]?.metaById ?? EMPTY_META);

export const useChatMessageById = (chatKey: ChatKey, messageId: number) =>
  useStore(chatMessagesStore, (s) => (s.chats[chatKey]?.byId ?? EMPTY_BY_ID)[messageId]);

export const useChatMessagesByIds = (chatKey: ChatKey, ids: number[]) =>
  useStoreWithEqualityFn(
    chatMessagesStore,
    (s) => {
      const chat = s.chats[chatKey];
      if (!chat || !ids.length) return [];
      const out: DirectMessage[] = [];
      for (const id of ids) {
        const m = chat.byId[id];
        if (m) out.push(m);
      }
      return out;
    },
    shallow
  );

export const getChatMessagesSnapshot = (chatKey: ChatKey): DirectMessage[] => {
  const chat = chatMessagesStore.getState().chats[chatKey];
  if (!chat || !chat.orderedIds.length) return [];
  const out: DirectMessage[] = [];
  for (const id of chat.orderedIds) {
    const m = chat.byId[id];
    if (m) out.push(m);
  }
  return out;
};

export const getChatMessagesTail = (chatKey: ChatKey, limit: number): DirectMessage[] => {
  const chat = chatMessagesStore.getState().chats[chatKey];
  if (!chat || !chat.orderedIds.length) return [];
  const out: DirectMessage[] = [];
  const lim = Math.max(0, Math.floor(limit || 0));
  const start = Math.max(0, chat.orderedIds.length - lim);
  for (let i = start; i < chat.orderedIds.length; i++) {
    const id = chat.orderedIds[i];
    const m = chat.byId[id];
    if (m) out.push(m);
  }
  return out;
};

export const getChatOldestServerId = (chatKey: ChatKey): number => {
  const chat = chatMessagesStore.getState().chats[chatKey];
  if (!chat || !chat.orderedIds.length) return 0;
  let minId = Number.MAX_SAFE_INTEGER;
  for (const id of chat.orderedIds) {
    const n = Number(id || 0) || 0;
    if (isServerId(n) && n < minId) minId = n;
  }
  return minId === Number.MAX_SAFE_INTEGER ? 0 : minId;
};

export const getChatMessageById = (chatKey: ChatKey, messageId: number): DirectMessage | undefined => {
  return chatMessagesStore.getState().chats[chatKey]?.byId?.[messageId];
};

export const getChatMetaById = (chatKey: ChatKey, messageId: number): MessageMeta | undefined => {
  return chatMessagesStore.getState().chats[chatKey]?.metaById?.[messageId];
};

export const getChatSliceOrEmpty = (chatKey: ChatKey): ChatSlice => {
  return chatMessagesStore.getState().chats[chatKey] || EMPTY_CHAT;
};
